import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../../services/api';
import { useAuth, useAuthActions } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/helpers';

function ProfileEdit() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { refreshUser } = useAuthActions();

  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    website: '',
    twitter: '',
    linkedin: '',
    github: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user) {
      setFormData({
        displayName: user.displayName || '',
        bio: user.bio || '',
        website: user.website || '',
        twitter: user.twitter || '',
        linkedin: user.linkedin || '',
        github: user.github || ''
      });
    }
  }, [isAuthenticated, user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear field errors when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }

    // Clear general error
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.displayName.trim()) {
      errors.displayName = 'Display name is required';
    } else if (formData.displayName.length > 100) {
      errors.displayName = 'Display name must be less than 100 characters';
    }

    if (formData.bio.length > 500) {
      errors.bio = 'Bio must be less than 500 characters';
    }

    if (formData.website && formData.website.length > 200) {
      errors.website = 'Website URL must be less than 200 characters';
    }

    if (formData.twitter && formData.twitter.length > 200) {
      errors.twitter = 'Twitter URL must be less than 200 characters';
    }

    if (formData.linkedin && formData.linkedin.length > 200) {
      errors.linkedin = 'LinkedIn URL must be less than 200 characters';
    }

    if (formData.github && formData.github.length > 200) {
      errors.github = 'GitHub URL must be less than 200 characters';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileData = {
        displayName: formData.displayName.trim(),
        bio: formData.bio.trim(),
        website: formData.website.trim(),
        twitter: formData.twitter.trim(),
        linkedin: formData.linkedin.trim(),
        github: formData.github.trim()
      };

      await userApi.updateProfile(profileData);
      
      // Refresh user data in context
      await refreshUser();
      
      // Navigate back to profile
      navigate('/profile');
      
    } catch (err) {
      console.error('Failed to update profile:', err);
      
      // Handle validation errors from server
      if (err.errors) {
        setFieldErrors(err.errors);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-edit-page">
      <div className="profile-edit-header">
        <h1>Edit Profile</h1>
        <p className="profile-edit-subtitle">Update your profile information</p>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="profile-edit-form">
        <div className="form-group">
          <label htmlFor="displayName">Display Name *</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Your display name"
              maxLength="100"
              required
            />
          {fieldErrors.displayName && (
            <span className="field-error">{fieldErrors.displayName}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="bio">Bio</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            className="form-textarea"
            placeholder="Tell us about yourself..."
            maxLength="500"
          />
          <div className={`character-counter ${formData.bio.length > 450 ? 'near-limit' : ''} ${formData.bio.length === 500 ? 'at-limit' : ''}`}>
            {formData.bio.length}/500 characters
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="website">Website</label>
          <input
            type="url"
            id="website"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            className="form-input"
            placeholder="https://your-website.com"
            maxLength="200"
          />
          {fieldErrors.website && (
            <span className="field-error">{fieldErrors.website}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="twitter">Twitter</label>
          <input
            type="url"
            id="twitter"
            name="twitter"
            value={formData.twitter}
            onChange={handleInputChange}
            className="form-input"
            placeholder="https://twitter.com/yourusername"
            maxLength="200"
          />
          {fieldErrors.twitter && (
            <span className="field-error">{fieldErrors.twitter}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="linkedin">LinkedIn</label>
          <input
            type="url"
            id="linkedin"
            name="linkedin"
            value={formData.linkedin}
            onChange={handleInputChange}
            className="form-input"
            placeholder="https://linkedin.com/in/yourusername"
            maxLength="200"
          />
          {fieldErrors.linkedin && (
            <span className="field-error">{fieldErrors.linkedin}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="github">GitHub</label>
          <input
            type="url"
            id="github"
            name="github"
            value={formData.github}
            onChange={handleInputChange}
            className="form-input"
            placeholder="https://github.com/yourusername"
            maxLength="200"
          />
          {fieldErrors.github && (
            <span className="field-error">{fieldErrors.github}</span>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-save"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Update Profile'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProfileEdit;